import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadAccess, type Access, type AccessContext } from './access/access.ts';
import { ROLE_LABEL, rolesLabel } from './access/roles.ts';
import { LinkConfirm } from './auth/LinkConfirm.tsx';
import { aalOf } from './auth/sharedSession.ts';
import { SignIn } from './auth/SignIn.tsx';
import { isPendingLink, type AuthOutcome } from './auth/authReturn.ts';
import { useSession } from './auth/useSession.ts';
import { Button } from './components/Button.tsx';
import { ErrorState, Loading, PoweredBy } from './components/States.tsx';
import type { WebConfig } from './config.ts';
import { HelpPanel } from './help/HelpPanel.tsx';
import { LegalLinks } from './legal/LegalPage.tsx';
import { LicenceGate } from './licences/LicenceGate.tsx';
import { canPortal, mfaStep, PILOT_PHONE_ONLY_WORDS } from '@teslyn/domain';
import { loadAssurance, loadRequirement, type Assurance, type Requirement } from './mfa/api.ts';
import { TwoStepCard, TwoStepCode, TwoStepEnrol } from './mfa/TwoStep.tsx';
import { previewAudit } from './portal/api.ts';
import type { PreviewTarget } from './portal/PreviewScreen.tsx';
import { GetMobileScreen } from './mobile-app/GetMobile.tsx';
import { Shell } from './shell/Shell.tsx';
import {
  applyThemeAndTitle,
  fetchTenantRow,
  mergeTenant,
  type Tenant,
  type TenantResult,
} from './tenant/tenant.ts';

export interface AppProps {
  readonly sb: SupabaseClient;
  readonly tenant: TenantResult;
  readonly config: WebConfig;
  /**
   * Why the sign-in this address brought back did not sign the person in (auth/authReturn.ts), an email link
   * waiting for its button, or null.
   */
  readonly authNotice?: AuthOutcome;
}

/** Where auth links return: this page, keeping `?tenant=` on preview hosts. */
function here(): string {
  return window.location.origin + window.location.pathname + window.location.search;
}

const CHOICE_KEY = 'teslyn.context';

function rememberedChoice(): string | null {
  try {
    return window.localStorage.getItem(CHOICE_KEY);
  } catch {
    return null;
  }
}
function remember(key: string | null): void {
  try {
    if (key) window.localStorage.setItem(CHOICE_KEY, key);
    else window.localStorage.removeItem(CHOICE_KEY);
  } catch {
    /* storage unavailable: the chooser shows again next time */
  }
}

export function App({ sb, tenant, config, authNotice = null }: AppProps): ReactElement {
  if (tenant.kind === 'unknown') return <UnknownPortal />;
  return <Portal sb={sb} initialTenant={tenant.tenant} config={config} authNotice={authNotice} />;
}

function Portal({
  sb,
  initialTenant,
  config,
  authNotice,
}: {
  readonly sb: SupabaseClient;
  readonly initialTenant: Tenant;
  readonly config: WebConfig;
  readonly authNotice: AuthOutcome;
}): ReactElement {
  const session = useSession(sb);
  const [tenant, setTenant] = useState(initialTenant);
  const [helpOpen, setHelpOpen] = useState(false);
  const [noticeSeen, setNoticeSeen] = useState(false);
  // an email link waits for its button (nothing spent until then); its outcome, if words, is the notice
  const [outcome, setOutcome] = useState<AuthOutcome>(authNotice);
  const notice = typeof outcome === 'string' ? outcome : null;

  if (isPendingLink(outcome)) return <LinkConfirm link={outcome} tenant={tenant} onDone={setOutcome} />;
  if (session.status === 'loading') {
    return (
      <div className="signin">
        <Loading what="your session" />
      </div>
    );
  }
  if (session.status === 'signed-out') {
    return (
      <div className={helpOpen ? 'signin-wrap signin-wrap--help' : 'signin-wrap'}>
        <SignIn
          sb={sb}
          tenant={tenant}
          auth={config.auth}
          redirectTo={here()}
          notice={noticeSeen ? null : notice}
          onHelp={() => setHelpOpen(true)}
        />
        {helpOpen && (
          <HelpPanel
            pageId="cloud.sign-in"
            roles={null}
            tenantName={tenant.name}
            productTitle={tenant.productTitle}
            onClose={() => setHelpOpen(false)}
          />
        )}
      </div>
    );
  }
  const user = session.session.user;
  // a sign-in link that came back while this browser was already signed in (security review M3: it never replaces
  // the sign-in): say what happened and let the person choose
  if (notice && !noticeSeen) {
    return (
      <div className="signin">
        <main className="signin__card" aria-labelledby="notice-title">
          {tenant.logo && <img className="signin__logo" src={tenant.logo} alt={tenant.name} />}
          <h1 id="notice-title" className="title">
            You are signed in as {user.email}
          </h1>
          <p className="field__error body" role="alert">
            {notice}
          </p>
          <Button variant="primary" onClick={() => setNoticeSeen(true)}>
            Carry on as {user.email}
          </Button>
          <Button
            variant="quiet"
            onClick={() => {
              setNoticeSeen(true);
              void sb.auth.signOut({ scope: 'local' });
            }}
          >
            Sign out
          </Button>
        </main>
        <footer className="signin__foot">
          <PoweredBy />
          <LegalLinks search={window.location.search} />
        </footer>
      </div>
    );
  }
  return (
    <SignedIn
      key={user.id}
      sb={sb}
      tenant={tenant}
      onTenant={setTenant}
      userId={user.id}
      email={user.email ?? ''}
      level={aalOf(session.session.access_token)}
    />
  );
}

function SignedIn({
  sb,
  tenant,
  onTenant,
  userId,
  email,
  level,
}: {
  readonly sb: SupabaseClient;
  readonly tenant: Tenant;
  readonly onTenant: (t: Tenant) => void;
  readonly userId: string;
  readonly email: string;
  /** The session's sign-in level; two-step sign-in finished on another address (decision 978) changes it here. */
  readonly level: 'aal1' | 'aal2';
}): ReactElement {
  const [access, setAccess] = useState<Access | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(rememberedChoice);
  // Preview as client (MB decision 9): never remembered, so a reload always returns to the staff member's own view.
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  // Two-step sign-in (docs/specs/cloud/mfa.md): the session's level, and which tenants require it of this person.
  const [mfa, setMfa] = useState<{ readonly assurance: Assurance; readonly requirement: Requirement } | null>(
    null,
  );
  const slug = tenant.slug;
  const source = tenant.source;

  const loadMfa = useCallback(() => {
    void Promise.all([loadAssurance(sb), loadRequirement(sb)])
      .then(([assurance, requirement]) => setMfa({ assurance, requirement }))
      .catch(() =>
        setMfa({ assurance: { aal: 'aal1', hasFactor: false }, requirement: { requiredTenants: [] } }),
      );
  }, [sb]);
  useEffect(loadMfa, [loadMfa, level]);

  const load = useCallback(() => {
    setError(null);
    setAccess(null);
    // Signed in, the tenant row is readable to members: its theme and id replace the bundled copy.
    void fetchTenantRow(sb, slug).then((row) => {
      if (row) {
        const t = mergeTenant(slug, source, row);
        applyThemeAndTitle(t);
        onTenant(t);
      }
    });
    loadAccess(sb, userId, slug)
      .then(setAccess)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, userId, slug, source, onTenant]);

  useEffect(load, [load]);

  const signOut = (): void => {
    remember(null);
    // this browser only: Teslyn Field and Teslyn Mobile, and other browsers, stay signed in ("Sign out everywhere"
    // on My sign-in ends them all; docs/specs/cloud/sign-in-security.md §6)
    void sb.auth.signOut({ scope: 'local' });
  };

  if (error) {
    return (
      <div className="signin">
        <ErrorState message={error} onRetry={load} />
        <Button onClick={signOut}>Sign out</Button>
      </div>
    );
  }
  if (!access || !mfa) {
    return (
      <div className="signin">
        <Loading what="your organisations" />
      </div>
    );
  }
  // An authenticator app is set up: its code is the second step of every sign-in.
  if (mfa.assurance.hasFactor && mfa.assurance.aal !== 'aal2') {
    return (
      <TwoStepCard tenant={tenant} title="Enter your sign-in code" onSignOut={signOut}>
        <p className="body">
          Open the authenticator app on your phone and enter the 6-digit code for Teslyn.
        </p>
        <TwoStepCode sb={sb} onDone={loadMfa} />
        <p className="caption muted">
          Lost your phone? Ask your tenant admin (or Braynewave) to reset your two-step sign-in.
        </p>
      </TwoStepCard>
    );
  }
  if (access.contexts.length === 0 && access.pilotOnly.length > 0) {
    return <PilotOnly sb={sb} email={email} tenant={tenant} onSignOut={signOut} />;
  }
  if (access.contexts.length === 0) {
    return <NotLinked email={email} tenant={tenant} elsewhere={access.elsewhere} onSignOut={signOut} />;
  }
  const current =
    access.contexts.length === 1
      ? access.contexts[0]!
      : (access.contexts.find((c) => c.key === chosen) ?? null);
  if (!current) {
    return (
      <Chooser
        tenant={tenant}
        contexts={access.contexts}
        onChoose={(c) => {
          remember(c.key);
          setChosen(c.key);
        }}
        onSignOut={signOut}
      />
    );
  }
  // Braynewave's console and Teslyn Support access always need two-step sign-in (decision 800)
  const required =
    current.kind === 'platform' ||
    (current.kind === 'workspace' &&
      (Boolean(current.support) || mfa.requirement.requiredTenants.includes(current.tenantId)));
  if (mfaStep({ ...mfa.assurance, required }) === 'enrol') {
    return (
      <TwoStepCard tenant={tenant} title="Set up two-step sign-in" onSignOut={signOut}>
        <p className="body">
          {current.kind === 'platform' || (current.kind === 'workspace' && current.support)
            ? current.kind === 'platform'
              ? 'Braynewave’s admin console needs two-step sign-in.'
              : 'Teslyn Support access needs two-step sign-in.'
            : `${tenant.name} requires two-step sign-in for your role.`}{' '}
          After this, you enter a code from your phone each time you sign in.
        </p>
        <TwoStepEnrol sb={sb} onDone={loadMfa} />
        {access.contexts.length > 1 && (
          <Button
            variant="quiet"
            onClick={() => {
              remember(null);
              setChosen(null);
            }}
          >
            Switch organisation
          </Button>
        )}
      </TwoStepCard>
    );
  }
  // the seat check at sign-in (licences spec §6.4): staff of an organisation only; off until go-live
  const switchOrg =
    access.contexts.length > 1
      ? () => {
          remember(null);
          setChosen(null);
        }
      : undefined;
  const gated = (shell: ReactElement): ReactElement =>
    current.kind === 'workspace' && !current.support ? (
      <LicenceGate
        key={current.key}
        sb={sb}
        tenant={tenant}
        tenantId={current.tenantId}
        tenantName={current.tenantName}
        roles={current.roles}
        onSignOut={signOut}
        {...(switchOrg ? { onSwitch: switchOrg } : {})}
      >
        {shell}
      </LicenceGate>
    ) : (
      shell
    );
  if (preview && current.kind === 'workspace') {
    const target = preview;
    const asClient: AccessContext = {
      kind: 'client',
      key: `preview:${target.clientOrgId}:${target.role}`,
      tenantId: current.tenantId,
      tenantSlug: current.tenantSlug,
      clientOrgId: target.clientOrgId,
      clientOrgName: target.clientOrgName,
      role: target.role,
    };
    return gated(
      <Shell
        key={asClient.key}
        sb={sb}
        tenant={tenant}
        userId={userId}
        email={email}
        context={asClient}
        redirectTo={here()}
        onSignOut={signOut}
        onSessionChanged={loadMfa}
        preview={{
          onEnd: () => {
            void previewAudit(sb, target.clientOrgId, 'ended').catch(() => undefined);
            setPreview(null);
          },
        }}
      />,
    );
  }
  return gated(
    <Shell
      key={current.key}
      sb={sb}
      tenant={tenant}
      userId={userId}
      email={email}
      context={current}
      {...(current.kind === 'workspace' && !current.support && canPortal(current.roles, 'portal.preview')
        ? { onPreview: setPreview }
        : {})}
      redirectTo={here()}
      onSignOut={signOut}
      onSessionChanged={loadMfa}
      {...(switchOrg ? { onSwitch: switchOrg } : {})}
    />,
  );
}

export function contextLabel(c: AccessContext): { readonly title: string; readonly detail: string } {
  switch (c.kind) {
    case 'workspace':
      return c.support
        ? { title: `Teslyn Support: ${c.tenantName}`, detail: 'Support access granted by the organisation' }
        : { title: `${c.tenantName} workspace`, detail: rolesLabel(c.roles) };
    case 'client':
      return { title: c.clientOrgName, detail: `Client portal · ${ROLE_LABEL[c.role]}` };
    case 'platform':
      return { title: 'Platform', detail: ROLE_LABEL.platform_admin };
  }
}

function Chooser({
  tenant,
  contexts,
  onChoose,
  onSignOut,
}: {
  readonly tenant: Tenant;
  readonly contexts: readonly AccessContext[];
  readonly onChoose: (c: AccessContext) => void;
  readonly onSignOut: () => void;
}): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="chooser-title">
        {tenant.logo && <img className="signin__logo" src={tenant.logo} alt={tenant.name} />}
        <h1 id="chooser-title" className="title">
          Choose where to work
        </h1>
        <p className="body muted">Your account has access to more than one area of {tenant.productTitle}.</p>
        <ul className="chooser">
          {contexts.map((c) => {
            const l = contextLabel(c);
            return (
              <li key={c.key}>
                <button type="button" className="chooser__item" onClick={() => onChoose(c)}>
                  <span className="heading">{l.title}</span>
                  <span className="caption muted">{l.detail}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <Button variant="quiet" onClick={onSignOut}>
          Sign out
        </Button>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search={window.location.search} />
      </footer>
    </div>
  );
}

/** A pilot-only sign-in (R156, decision 917): Teslyn Mobile only, so Teslyn Cloud says where to go instead. */
function PilotOnly({
  sb,
  email,
  tenant,
  onSignOut,
}: {
  readonly sb: SupabaseClient;
  readonly email: string;
  readonly tenant: Tenant;
  readonly onSignOut: () => void;
}): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="pilot-title">
        {tenant.logo && <img className="signin__logo" src={tenant.logo} alt={tenant.name} />}
        <h1 id="pilot-title" className="title">
          Use Teslyn Mobile on your phone
        </h1>
        <p className="body">
          You are signed in as <strong>{email}</strong>. {PILOT_PHONE_ONLY_WORDS}
        </p>
        <p className="body muted">Install Teslyn Mobile below, then sign in there with this address.</p>
        {/* decision 912: the download is for signed-in people; a pilot's account is the phone app */}
        <GetMobileScreen sb={sb} />
        <Button onClick={onSignOut}>Sign out</Button>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search={window.location.search} />
      </footer>
    </div>
  );
}

function NotLinked({
  email,
  tenant,
  elsewhere,
  onSignOut,
}: {
  readonly email: string;
  readonly tenant: Tenant;
  readonly elsewhere: readonly string[];
  readonly onSignOut: () => void;
}): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="notlinked-title">
        {tenant.logo && <img className="signin__logo" src={tenant.logo} alt={tenant.name} />}
        <h1 id="notlinked-title" className="title">
          Your account is not linked to an organisation yet
        </h1>
        <p className="body">
          You are signed in as <strong>{email}</strong>, but this address has no access to{' '}
          {tenant.productTitle}. Ask the person who invited you to check the address and your role.
        </p>
        {elsewhere.length > 0 && (
          <p className="body muted">
            This account belongs to {elsewhere.join(', ')}. Sign in on that organisation&apos;s portal
            address.
          </p>
        )}
        <Button onClick={onSignOut}>Sign out</Button>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search={window.location.search} />
      </footer>
    </div>
  );
}

function UnknownPortal(): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="unknown-title">
        <h1 id="unknown-title" className="title">
          This portal address is not set up
        </h1>
        <p className="body">
          No organisation uses <span className="data">{window.location.hostname}</span>. Check the address in
          your invitation email.
        </p>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search={window.location.search} />
      </footer>
    </div>
  );
}
